import { AxiosInstance } from 'axios';
import { map } from 'lodash-es';
import { HostSitemap, ListWithTotals, OneUserFilters, UserDto, UserFilter, UpdateUserDto, AccountsPrivacyOptionsEnum, UnsubscribeUserFromNewsletterDto } from '@sweatpals/shared';
import { UserDataType } from './types/user-types';
import { UsersService as BaseUsersService } from '@sweatpals/data-services';

export class UserService {
	private readonly httpClient: AxiosInstance;
	private readonly baseService: BaseUsersService;

	constructor(httpClient: AxiosInstance) {
		this.httpClient = httpClient;
		this.baseService = new BaseUsersService(httpClient);
	}

	public async findAllPublic(filter: UserFilter): Promise<ListWithTotals<UserDataType>> {
		const { data } = await this.baseService.findAllPublic(filter);

		data.list = data.list.map(user => this._buildDataFromResponse(user));

		return data as ListWithTotals<UserDataType>;
	}

	public async findAll(filter: UserFilter): Promise<ListWithTotals<UserDataType>> {
		const { data } = await this.baseService.findAll(filter);

		data.list = data.list.map(user => this._buildDataFromResponse(user));

		return data as ListWithTotals<UserDataType>;
	}

	public async findPublicIdByUsername(userName: string): Promise<string | null> {
		if (!userName || userName === 'undefined')
			return null;

		const { data } = await this.baseService.findPublicIdByUsername(userName);

		return data;
	}

	public async findPublicUserNameById(id: string): Promise<string | null> {
		if (!id || id === 'undefined')
			return null;

		const { data } = await this.baseService.findPublicUserNameById(id);

		return data;
	}

	public async getUserByIdPublic(userId?: string, filters?: OneUserFilters): Promise<UserDataType | undefined> {
		if (!userId || userId === 'undefined') return;

		const { data } = await this.baseService.getByIdPublic(userId, filters);

		return this._buildDataFromResponse(data);
	}

	public async updateUser(updateData: UpdateUserDto): Promise<UserDataType> {
		const { data } = await this.baseService.updateCurrent(updateData);

		return this._buildDataFromResponse(data);
	}

	public async getCurrentUser(): Promise<UserDataType> {
		const { data } = await this.baseService.getCurrent();

		return this._buildDataFromResponse(data);
	}

	public async deleteUserFull(userId: string): Promise<void> {
		await this.baseService.deleteFull(userId);
	}

	public async getByUsername(nickname: string, filters?: OneUserFilters): Promise<UserDataType | null> {
		if (!nickname || nickname === 'undefined') return null;

		const { data } = await this.baseService.getByUsername(nickname, filters);

		return this._buildDataFromResponse(data);
	}

	public async getUserProfileChatInvitationCodePublic(userId: string): Promise<string | null> {
		const { data } = await this.baseService.getUserProfileChatInvitationCodePublic(userId);

		return data;
	}

	public async verifyUserNameCanBeUsed(userName: string): Promise<true> {
		const { data } = await this.baseService.verifyUserNameCanBeUsed(userName);

		return data;
	}

	public async getUserCommunityProfiles(userId: string): Promise<ListWithTotals<UserDataType>> {
		const { data } = await this.baseService.getUserCommunityProfiles(userId);

		data.list = data.list?.map((user) => this._buildDataFromResponse(user));

		return data as ListWithTotals<UserDataType>;
	}

	public async getUsersSitemap(filter: UserFilter): Promise<HostSitemap[]> {
		const { data } = await this.baseService.getUsersSitemap({
			...filter,
			profilePrivacy: AccountsPrivacyOptionsEnum.Public,
			isCommunityProfile: true,
		});

		return data;
	}

	public async getSitemapUsersCount(): Promise<number> {
		const { data } = await this.baseService.getPublicUsersCount({
			profilePrivacy: AccountsPrivacyOptionsEnum.Public,
			isCommunityProfile: true,
		});

		return data;
	}

	public async addToNewsletter(email: string): Promise<void> {
		await this.baseService.addToNewsletter(email);
	}

	public async removeUserFromNewsletter(data: UnsubscribeUserFromNewsletterDto): Promise<void> {
		await this.httpClient.post('/users/remove-from-newsletter', data);
	}

	public async verifyUserIsUnsubscribedFromNewsletter(data: Omit<UnsubscribeUserFromNewsletterDto, 'reason'>): Promise<boolean> {
		const { data: result } = await this.httpClient.post('/users/verify-user-is-unsubscribed-from-newsletter', data);
		return result;
	}

	public async getProfileShortLink(profileId: string): Promise<string> {
		if (!profileId || profileId === 'undefined') return '';

		const { data } = await this.httpClient.get<string>(`/users/public/profile/deeplink/${profileId}`);

		return data;
	}

	public async getCommunityBranchLink(userId: string, fallbackToStore?: boolean): Promise<string> {
		if (!userId || userId === 'undefined') return '';

		const { data } = await this.httpClient.get<string>(`/users/profile/branchlink/${userId}`, { params: { store: fallbackToStore } });

		return data;
	}

	private _buildDataFromResponse(data: UserDto): UserDataType {
		if (!data) {
			throw new Error('User data is null');
		};

		return {
			...data,
			url: (data as any).url ?? null,
			userInterests: map(data.interests, interest => ({
				id: interest.interestId ?? null,
				iconId: interest.interest.iconId ?? null,
				name: interest.interest.name ?? null,
				masteryLevel: interest.masteryLevel ?? null,
				priority: interest.interest.hasPriorityOverNativeAsset ?? null,
			})),
		};
	}
}
